import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr
} from '@chakra-ui/react'
import { FC, useEffect, useState } from 'react'
import { MdDelete, MdUpload, MdWarning } from 'react-icons/md'

import {
  SimilarProductRow,
  validateRows
} from '../../../utils/parametrization/similarProductCsv'

interface Props {
  isOpen: boolean
  initialRows: SimilarProductRow[]
  onClose: () => void
  onConfirm: (rows: SimilarProductRow[]) => void
  isLoading?: boolean
}

const UploadPreviewModal: FC<Props> = ({
  isOpen,
  initialRows,
  onClose,
  onConfirm,
  isLoading = false
}) => {
  const [rows, setRows] = useState<SimilarProductRow[]>([])
  const [onlyErrors, setOnlyErrors] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setRows(validateRows(initialRows))
      setOnlyErrors(false)
    }
  }, [isOpen, initialRows])

  const updateField = (
    lineNumber: number,
    field: keyof SimilarProductRow,
    value: string
  ) => {
    const updated = rows.map(r =>
      r._lineNumber === lineNumber ? { ...r, [field]: value } : r
    )
    setRows(validateRows(updated))
  }

  const removeRow = (lineNumber: number) => {
    setRows(validateRows(rows.filter(r => r._lineNumber !== lineNumber)))
  }

  const removeAllWithErrors = () => {
    const cleaned = validateRows(rows.filter(r => r.errors.length === 0))
    setRows(cleaned)
    setOnlyErrors(false)
  }

  const totalErrors = rows.reduce((acc, r) => acc + r.errors.length, 0)
  const hasErrors = totalErrors > 0
  const errorRows = rows.filter(r => r.errors.length > 0)
  const displayedRows = onlyErrors ? errorRows : rows

  const inp = {
    size: 'sm' as const,
    borderRadius: 'md',
    borderColor: 'gray.300',
    _focus: { borderColor: '#0f5238', boxShadow: '0 0 0 1px #0f5238' }
  }

  const errInp = { ...inp, borderColor: 'red.300' }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="full">
      <ModalOverlay />
      <ModalContent
        maxW="1400px"
        mx="auto"
        my={4}
        maxH="calc(100vh - 32px)"
        display="flex"
        flexDirection="column"
      >
        <ModalHeader
          px={4}
          py={3}
          bg="#0f5238"
          color="white"
          borderTopRadius={5}
          borderBottom="3px solid #ECC94B"
          fontSize="15px"
          fontWeight="700"
          flexShrink={0}
        >
          <Flex align="center" gap={3}>
            <Icon as={MdUpload} boxSize={5} />
            Preview de Importação — Produtos Similares
            {hasErrors ? (
              <Badge bg="red.500" color="white" borderRadius="full" px={2}>
                {totalErrors} {totalErrors === 1 ? 'erro' : 'erros'}
              </Badge>
            ) : rows.length > 0 ? (
              <Badge bg="green.400" color="white" borderRadius="full" px={2}>
                sem erros
              </Badge>
            ) : null}
          </Flex>
        </ModalHeader>
        <ModalCloseButton color="white" top={3} />

        <ModalBody
          bg="white"
          py={3}
          px={4}
          flex={1}
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <Flex align="center" gap={2} mb={2} flexWrap="wrap">
            <Text fontSize="12px" color="gray.500">
              Revise os dados antes de confirmar. Linhas em vermelho possuem
              erros — corrija os campos ou remova a linha para habilitar a
              importação.
            </Text>
            {hasErrors && (
              <>
                <Button
                  size="xs"
                  colorScheme="red"
                  variant="outline"
                  onClick={() => setOnlyErrors(f => !f)}
                  flexShrink={0}
                >
                  {onlyErrors
                    ? `Mostrar todos (${rows.length})`
                    : `Filtrar erros (${errorRows.length})`}
                </Button>
                <Button
                  size="xs"
                  colorScheme="red"
                  variant="solid"
                  onClick={removeAllWithErrors}
                  flexShrink={0}
                >
                  Remover todos com erros
                </Button>
              </>
            )}
          </Flex>

          <Box
            flex={1}
            overflow="auto"
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="md"
          >
            <Table size="sm" sx={{ 'td, th': { px: '8px', py: '6px' } }}>
              <Thead bg="gray.50" position="sticky" top={0} zIndex={2}>
                <Tr>
                  <Th fontSize="9px" px={2} py={2} minW="55px">
                    Id
                  </Th>
                  <Th fontSize="9px" px={2} py={2} minW="70px">
                    Emp. Id *
                  </Th>
                  <Th fontSize="9px" px={2} py={2} minW="120px">
                    Empresa - Nome *
                  </Th>
                  <Th fontSize="9px" px={2} py={2} minW="85px">
                    Prod. Novo Id *
                  </Th>
                  <Th fontSize="9px" px={2} py={2} minW="140px">
                    Prod. Novo - Nome *
                  </Th>
                  <Th fontSize="9px" px={2} py={2} minW="85px">
                    Prod. Sim. Id *
                  </Th>
                  <Th fontSize="9px" px={2} py={2} minW="140px">
                    Prod. Similar - Nome *
                  </Th>
                  <Th fontSize="9px" px={2} py={2} minW="70px">
                    Demanda %
                  </Th>
                  <Th fontSize="9px" px={2} py={2} minW="120px">
                    Data Início *
                  </Th>
                  <Th fontSize="9px" px={2} py={2} minW="120px">
                    Data Fim *
                  </Th>
                  <Th fontSize="9px" px={2} py={2} minW="100px">
                    Usuário
                  </Th>
                  <Th px={2} py={2} w="32px"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {displayedRows.map(row => {
                  const hasRowError = row.errors.length > 0
                  const rowBg = hasRowError ? 'red.50' : undefined
                  const rowHover = hasRowError ? 'red.50' : 'gray.50'

                  const fieldErr = (field: string) =>
                    row.errors.some(e =>
                      e.toLowerCase().includes(field.toLowerCase())
                    )

                  return (
                    <Tr
                      key={row._lineNumber}
                      bg={rowBg}
                      _hover={{ bg: rowHover }}
                      borderBottom="1px solid"
                      borderColor="gray.100"
                    >
                      {/* Id */}
                      <Td>
                        <Input
                          {...inp}
                          value={row.id}
                          onChange={e =>
                            updateField(row._lineNumber, 'id', e.target.value)
                          }
                        />
                      </Td>

                      {/* Empresa - Id */}
                      <Td>
                        <Input
                          {...(fieldErr('empresa - id') ? errInp : inp)}
                          value={row.empresaId}
                          onChange={e =>
                            updateField(
                              row._lineNumber,
                              'empresaId',
                              e.target.value
                            )
                          }
                        />
                      </Td>

                      {/* Empresa - Nome */}
                      <Td>
                        <Input
                          {...(fieldErr('empresa - nome') ? errInp : inp)}
                          value={row.empresaNome}
                          onChange={e =>
                            updateField(
                              row._lineNumber,
                              'empresaNome',
                              e.target.value
                            )
                          }
                        />
                      </Td>

                      {/* Produto Novo - Id */}
                      <Td>
                        <Flex align="center" gap={1}>
                          <Input
                            {...(fieldErr('produto novo - id') ||
                            fieldErr('violação 1:1')
                              ? errInp
                              : inp)}
                            value={row.produtoNovoId}
                            onChange={e =>
                              updateField(
                                row._lineNumber,
                                'produtoNovoId',
                                e.target.value
                              )
                            }
                          />
                          {hasRowError && (
                            <Tooltip
                              label={
                                <Box>
                                  {row.errors.map((err, i) => (
                                    <Text key={i} fontSize="12px">
                                      • {err}
                                    </Text>
                                  ))}
                                </Box>
                              }
                              hasArrow
                              placement="top"
                              bg="red.700"
                            >
                              <Box cursor="default" flexShrink={0}>
                                <Icon
                                  as={MdWarning}
                                  color="red.500"
                                  boxSize={4}
                                />
                              </Box>
                            </Tooltip>
                          )}
                        </Flex>
                      </Td>

                      {/* Produto Novo - Nome */}
                      <Td>
                        <Input
                          {...(fieldErr('produto novo - nome') ? errInp : inp)}
                          value={row.produtoNovoNome}
                          onChange={e =>
                            updateField(
                              row._lineNumber,
                              'produtoNovoNome',
                              e.target.value
                            )
                          }
                        />
                      </Td>

                      {/* Produto Similar - Id */}
                      <Td>
                        <Input
                          {...(fieldErr('produto similar - id') ||
                          fieldErr('violação 1:1')
                            ? errInp
                            : inp)}
                          value={row.produtoSimilarId}
                          onChange={e =>
                            updateField(
                              row._lineNumber,
                              'produtoSimilarId',
                              e.target.value
                            )
                          }
                        />
                      </Td>

                      {/* Produto Similar - Nome */}
                      <Td>
                        <Input
                          {...(fieldErr('produto similar - nome')
                            ? errInp
                            : inp)}
                          value={row.produtoSimilarNome}
                          onChange={e =>
                            updateField(
                              row._lineNumber,
                              'produtoSimilarNome',
                              e.target.value
                            )
                          }
                        />
                      </Td>

                      {/* Demanda */}
                      <Td>
                        <Input
                          {...inp}
                          value={row.demanda}
                          onChange={e =>
                            updateField(
                              row._lineNumber,
                              'demanda',
                              e.target.value
                            )
                          }
                        />
                      </Td>

                      {/* Data Início */}
                      <Td>
                        <Input
                          {...(fieldErr('data - inicio') ? errInp : inp)}
                          type="date"
                          value={row.dataInicio}
                          onChange={e =>
                            updateField(
                              row._lineNumber,
                              'dataInicio',
                              e.target.value
                            )
                          }
                        />
                      </Td>

                      {/* Data Fim */}
                      <Td>
                        <Input
                          {...(fieldErr('data - fim') ? errInp : inp)}
                          type="date"
                          value={row.dataFim}
                          onChange={e =>
                            updateField(
                              row._lineNumber,
                              'dataFim',
                              e.target.value
                            )
                          }
                        />
                      </Td>

                      {/* Usuário */}
                      <Td>
                        <Input
                          {...inp}
                          value={row.usuario}
                          onChange={e =>
                            updateField(
                              row._lineNumber,
                              'usuario',
                              e.target.value
                            )
                          }
                        />
                      </Td>

                      {/* Remover */}
                      <Td>
                        <Tooltip label="Remover linha" hasArrow placement="top">
                          <Button
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => removeRow(row._lineNumber)}
                          >
                            <Icon as={MdDelete} boxSize={4} />
                          </Button>
                        </Tooltip>
                      </Td>
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
          </Box>

          <Flex justify="space-between" align="center" mt={2} flexShrink={0}>
            <Text fontSize="12px" color="gray.500">
              {rows.length} registro{rows.length !== 1 ? 's' : ''}
              {hasErrors && (
                <Text as="span" color="red.500" fontWeight="600">
                  {' '}
                  · {totalErrors} erro{totalErrors !== 1 ? 's' : ''} encontrado
                  {totalErrors !== 1 ? 's' : ''}
                </Text>
              )}
            </Text>
            {hasErrors && (
              <Text fontSize="12px" color="red.500">
                Corrija ou remova todas as linhas com erro para confirmar.
              </Text>
            )}
          </Flex>
        </ModalBody>

        <ModalFooter bg="white" borderBottomRadius={5} gap={2} flexShrink={0}>
          <Button
            variant="outline"
            borderColor="gray.300"
            color="gray.600"
            onClick={onClose}
            isDisabled={isLoading}
          >
            Cancelar
          </Button>
          <Tooltip
            label={
              hasErrors
                ? 'Resolva todos os erros antes de confirmar'
                : rows.length === 0
                ? 'Nenhum registro para importar'
                : undefined
            }
            isDisabled={!hasErrors && rows.length > 0}
            hasArrow
          >
            <Button
              bg="#0f5238"
              color="white"
              _hover={{ bg: '#1d5840' }}
              leftIcon={<Icon as={MdUpload} />}
              isDisabled={hasErrors || rows.length === 0}
              isLoading={isLoading}
              loadingText="Importando..."
              onClick={() => onConfirm(rows)}
            >
              Confirmar Importação
            </Button>
          </Tooltip>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default UploadPreviewModal
